import { useEffect, useRef, useState } from 'react'
import './LocationAutocomplete.css'

/**
 * Street address autocomplete for the seller Location field (Winnie's review,
 * A2, then again on 24 August: suburb and state alone is not an address).
 *
 * Uses Google Places when `VITE_GOOGLE_MAPS_API_KEY` is configured, and
 * degrades to an ordinary text input when it is not — the field keeps working
 * either way, it just stops suggesting. That matters because the key needs a
 * Google Cloud project with billing enabled, which is a business decision, and
 * the platform should not have a broken form field while that is pending.
 *
 * Results are restricted to Australian localities, which is what a seller
 * location actually is here — a full street address is neither wanted nor
 * useful for routing an inspection.
 */

interface Props {
  value: string
  onChange: (value: string) => void
  id?: string
  name?: string
  placeholder?: string
  required?: boolean
}

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined
const SCRIPT_ID = 'google-places-script'

let loaderPromise: Promise<boolean> | null = null

/** Load the Places library once per page, whatever calls for it first. */
function loadPlaces(): Promise<boolean> {
  if (!API_KEY) return Promise.resolve(false)
  if ((window as any).google?.maps?.places) return Promise.resolve(true)
  if (loaderPromise) return loaderPromise

  loaderPromise = new Promise<boolean>((resolve) => {
    const existing = document.getElementById(SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve(true))
      existing.addEventListener('error', () => resolve(false))
      return
    }
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.defer = true
    script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places`
    script.onload = () => resolve(true)
    script.onerror = () => {
      console.warn('[DirectAuto] Google Places failed to load — falling back to plain text entry.')
      resolve(false)
    }
    document.head.appendChild(script)
  })

  return loaderPromise
}

export default function LocationAutocomplete({ value, onChange, id, name, placeholder, required }: Props) {
  const [ready, setReady] = useState(false)
  const [suggestions, setSuggestions] = useState<{ description: string; placeId: string }[]>([])
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(-1)
  const serviceRef = useRef<any>(null)
  const boxRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    loadPlaces().then((ok) => {
      if (cancelled || !ok) return
      try {
        serviceRef.current = new (window as any).google.maps.places.AutocompleteService()
        setReady(true)
      } catch {
        setReady(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Close on an outside click, so the list never hangs over the rest of the form.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const fetchSuggestions = (input: string) => {
    if (!ready || !serviceRef.current || input.trim().length < 3) {
      setSuggestions([])
      setOpen(false)
      return
    }
    serviceRef.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: 'au' },
        // Winnie's review: the field is the seller's address on the Bill of
        // Sale, so it has to be the street address. '(cities)' only ever
        // returned suburb and state, which is what she was seeing.
        types: ['address'],
      },
      (predictions: any[], status: string) => {
        if (status !== 'OK' || !predictions) {
          setSuggestions([])
          setOpen(false)
          return
        }
        setSuggestions(
          predictions.slice(0, 6).map((p) => ({ description: p.description, placeId: p.place_id }))
        )
        setOpen(true)
        setHighlight(-1)
      }
    )
  }

  const pick = (description: string) => {
    // Google returns "Bellevue Hill NSW, Australia"; the country adds nothing here.
    onChange(description.replace(/,\s*Australia$/i, ''))
    setOpen(false)
    setSuggestions([])
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault()
      pick(suggestions[highlight].description)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className="loc-auto" ref={boxRef}>
      <input
        id={id}
        name={name}
        type="text"
        autoComplete="off"
        value={value}
        placeholder={placeholder || 'Start typing a street address…'}
        required={required}
        onChange={(e) => {
          onChange(e.target.value)
          fetchSuggestions(e.target.value)
        }}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
      />

      {open && suggestions.length > 0 && (
        <ul className="loc-list" role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={s.placeId}
              role="option"
              aria-selected={i === highlight}
              className={i === highlight ? 'is-active' : ''}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => {
                e.preventDefault()
                pick(s.description)
              }}
            >
              {s.description.replace(/,\s*Australia$/i, '')}
            </li>
          ))}
        </ul>
      )}

      {!API_KEY && (
        <small className="loc-note">
          Address suggestions are off — add a Google Maps API key to switch them on. Typing the address
          by hand works exactly as before.
        </small>
      )}
    </div>
  )
}
